import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import api from '@seeneco-ui/api';

import { getURLParameterByName } from '../../../../shared/utils';
import { useAuthResult } from '../../hooks';
import {
	FormTab,
	FormRoute,
	tabsDataSource,
} from './model';
import XAuthForm, { AuthFormViewProps as XAuthFormProps } from './auth-form.view';


type AuthFormProps = {
	tariffCode: string;
}
& Pick<XAuthFormProps,
	'loginTitle'
	| 'registrationTitle'
	| 'provider'
	| 'businessService'
	| 'systemName'
	| 'postActionURL'>

const AuthForm: React.SFC<AuthFormProps> = (props) => {
	const {
		provider,
		businessService,
		systemName,
		postActionURL,
		tariffCode,
	} = props;
	const [tab, setTab] = useState<FormTab>(FormTab.SIGN_IN);
	const [routeKey, setRouteKey] = useState<FormRoute>(FormRoute.LOGIN);
	const [login, setLogin] = useState<string>('');
	const [password, setPassword] = useState<string>('');
	const [confirmPassword, setConfirmPassword] = useState<string>('');
	const [passwordRecoveryCode, setPasswordRecoveryCode] = useState<string>('');
	const [email, setEmail] = useState<string>('');
	const [phoneNumber, setPhoneNumber] = useState<string>('');
	const [contactName, setContactName] = useState<string>('');
	const [isSubmitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(true);
	const [recoveryRequestMessage, setRecoveryRequestMessage] = useState<SuccessMessage>(null);
	const [recoveryResultMessage, setRecoveryResultMessage] = useState<SuccessMessage>(null);
	const [isFetching, setIsFetching] = useState<boolean>(false);
	const [registrationError, setRegistrationError] = useState<string>('');
	const [authResult] = useAuthResult();

	useEffect(() => {
		const login = getURLParameterByName('login');
		const passwordRecoveryCode = getURLParameterByName('passwordRecoveryCode');

		if (login) {
			setLogin(login);
		}

		if (passwordRecoveryCode) {
			setPasswordRecoveryCode(passwordRecoveryCode);
			setRouteKey(FormRoute.CHANGE_PASSWORD);
		}
	}, []);

	const handleRequestRecoveryPassword = useCallback((login: string) => {
		setIsFetching(true);
		api.platform.auth.security.authenticationService.requestPasswordRecovery(login).then(message => {
			setRecoveryRequestMessage(message);
			setIsFetching(false);
		});
	}, []);

	const handleRecoveryPassword = useCallback((login: string, password: string) => {
		setIsFetching(true);
		api.platform.auth.security.authenticationService.recoverPassword(passwordRecoveryCode, password, login).then(message => {
			setRecoveryResultMessage(message);
			setIsFetching(false);
		});
	}, [passwordRecoveryCode]);

	const handleChangeTab = (tab: FormTab) => {
		if (tab === FormTab.SIGN_IN) {
			setRouteKey(FormRoute.LOGIN);
		} else {
			setRouteKey(FormRoute.REGISTRATION);
		}
	
		setTab(tab);
	};

	const handleRegistration = (email: string, phoneNumber: string, contactName: string) => {
		api.platform.auth.security.registrationService.registerAccount({
			login: email,
			phoneNumber,
			contactName,
			provider,
			businessService,
			postActionURL,
			tariffCode,
			clientApplication: 'web',
		})
		.then(isValid => {
			if (!isValid) {
				setRegistrationError('Такой e-mail уже занят');
			}
		})
		.catch(err => setRegistrationError(err));
	};

	return (
		<XAuthForm
			{...props}
			login={login}
			password={password}
			confirmPassword={confirmPassword}
			email={email}
			phoneNumber={phoneNumber}
			contactName={contactName}
			isSubmitButtonDisabled={isSubmitButtonDisabled}
			tab={tab}
			routeKey={routeKey}
			recoveryRequestMessage={recoveryRequestMessage}
			recoveryResultMessage={recoveryResultMessage}
			isFetching={isFetching}
			businessService={businessService}
			systemName={systemName}
			authResult={authResult}
			registrationError={registrationError}
			tabsDataSource={tabsDataSource}
			onSetLogin={setLogin}
			onSetPassword={setPassword}
			onSetConfirmPassword={setConfirmPassword}
			onSetEmail={setEmail}
			onSetPhoneNumber={setPhoneNumber}
			onSetContactName={setContactName}
			onSetSubmitButtonDisabled={setSubmitButtonDisabled}
			onSetRouteKey={setRouteKey}
			onRequestRecoveryPassword={handleRequestRecoveryPassword}
			onRecoveryPassword={handleRecoveryPassword}
			onChangeTab={handleChangeTab}
			onRegistration={handleRegistration}
		/>
	);
}

export default AuthForm;
